import { forwardRef } from 'react'
import type { InputHTMLAttributes } from 'react'
import { cn } from '../../lib/utils'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean | string
  label?: string
  helperText?: string
  fullWidth?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, error, label, helperText, fullWidth = true, disabled, id, required, ...props }, ref) => {
    const hasError = !!error
    const errorMessage = typeof error === 'string' ? error : undefined
    const inputId = id || (label ? `input-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined)

    const inputElement = (
      <input
        ref={ref}
        id={inputId}
        disabled={disabled}
        aria-invalid={hasError}
        aria-describedby={
          hasError ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
        }
        className={cn(
          // Base styles
          'w-full px-3 py-2 text-sm text-text-muted',
          'transition-colors duration-200',
          'placeholder:text-text-muted/50',
          // Mobile: full border
          'border border-border rounded-md',
          'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
          // Desktop: bottom-border only style
          'md:border-0 md:border-b md:rounded-none',
          'md:hover:bg-input-hover',
          'md:focus:border-b-2 md:focus:border-primary md:focus:ring-0 md:focus:bg-input-hover',
          // Error state
          hasError && 'border-danger md:border-danger focus:border-danger focus:ring-danger/20',
          // Disabled state
          disabled && 'bg-surface cursor-not-allowed opacity-50',
          className
        )}
        {...props}
      />
    )

    // If no label, just return the input
    if (!label) {
      return inputElement
    }

    // With label, wrap in a container
    return (
      <div className={cn('flex flex-col', fullWidth && 'w-full')}>
        <label htmlFor={inputId} className="flex items-center gap-1 text-sm font-medium text-text mb-1">
          {label}
          {required && <span className="text-danger">*</span>}
        </label>

        {inputElement}

        {helperText && !hasError && (
          <p id={`${inputId}-helper`} className="text-sm text-text-muted mt-1">
            {helperText}
          </p>
        )}

        {errorMessage && (
          <p id={`${inputId}-error`} className="text-sm text-danger mt-1" role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input
